import React, { useState, useMemo, useCallback } from 'react';
import styles from './SettingsSection.module.css';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface ExportableItem {
  key: string;
  [k: string]: any;
}

interface SettingsSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Super-admin export support */
  exportable?: boolean;
  items?: ExportableItem[];
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  description,
  children,
  collapsible = false,
  defaultExpanded = true,
  exportable = false,
  items = [],
  selectedKeys,
  onSelectionChange,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const allSelected = useMemo(() => {
    if (!exportable || !selectedKeys || items.length === 0) return false;
    return items.every(item => selectedKeys.has(item.key));
  }, [exportable, selectedKeys, items]);

  const someSelected = useMemo(() => {
    if (!exportable || !selectedKeys || items.length === 0) return false;
    return items.some(item => selectedKeys.has(item.key)) && !allSelected;
  }, [exportable, selectedKeys, items, allSelected]);

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange || !selectedKeys) return;
    const next = new Set(selectedKeys);
    if (allSelected) {
      items.forEach(item => next.delete(item.key));
    } else {
      items.forEach(item => next.add(item.key));
    }
    onSelectionChange(next);
  }, [onSelectionChange, selectedKeys, allSelected, items]);

  return (
    <div className={styles.section}>
      <div
        className={`${styles.sectionHeader} ${collapsible ? styles.clickable : ''}`}
        onClick={collapsible ? () => setExpanded(!expanded) : undefined}
      >
        <div className={styles.headerLeft}>
          {collapsible && (
            <span className={styles.chevron}>
              {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </span>
          )}
          <div>
            <h3 className={styles.sectionTitle}>{title}</h3>
            {description && <p className={styles.sectionDescription}>{description}</p>}
          </div>
        </div>

        {exportable && expanded && items.length > 0 && (
          <div className={styles.headerRight} onClick={(e) => e.stopPropagation()}>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={handleSelectAll}
                className={styles.checkbox}
              />
              <span className={styles.selectAllText}>Select all</span>
            </label>
          </div>
        )}
      </div>

      {expanded && (
        <div className={styles.sectionContent}>
          {children}
        </div>
      )}
    </div>
  );
};

export default SettingsSection;
